import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-background-200/70 border-t border-background-300/60">
      {/* Main footer */}
      <div className="w-full px-4 md:px-6 lg:px-10 py-12 md:py-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {/* Brand */}
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 bg-primary-500 rounded-lg flex items-center justify-center">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-truck-line text-background-50 text-lg" />
                  </div>
                </div>
                <div className="flex flex-col">
                  <span className="font-heading font-bold text-base tracking-tight text-foreground-950">SPEED</span>
                  <span className="font-heading font-semibold text-[10px] tracking-[0.2em] -mt-1 text-primary-500">COURIER</span>
                </div>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                Speed Courier - Logística em Geral. Mais de 15 anos transportando cargas e encomendas com segurança e pontualidade em todo o Espírito Santo.
              </p>
              {/* Social links */}
              <div className="flex items-center gap-3">
                <a
                  href="https://wa.me/552737236099"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 bg-accent-500 hover:bg-accent-600 rounded-md flex items-center justify-center text-background-50 transition-colors"
                  aria-label="WhatsApp"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line" />
                  </div>
                </a>
                <a
                  href="https://www.facebook.com/SpeedCourier"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 bg-primary-500 hover:bg-primary-600 rounded-md flex items-center justify-center text-background-50 transition-colors"
                  aria-label="Facebook"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-facebook-fill" />
                  </div>
                </a>
                <a
                  href="https://www.instagram.com/speed.courier"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 bg-gradient-to-br from-purple-500 to-pink-500 hover:opacity-90 rounded-md flex items-center justify-center text-background-50 transition-opacity"
                  aria-label="Instagram"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-instagram-line" />
                  </div>
                </a>
                <a
                  href="mailto:speed@speedcourier.com.br"
                  className="w-9 h-9 bg-secondary-500 hover:bg-secondary-600 rounded-md flex items-center justify-center text-background-50 transition-colors"
                  aria-label="Email"
                  rel="nofollow"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line" />
                  </div>
                </a>
              </div>
            </div>

            {/* Quick links */}
            <div>
              <h4 className="font-heading font-semibold text-sm text-foreground-950 uppercase tracking-wider mb-4">
                {t('footer.links')}
              </h4>
              <ul className="space-y-2.5">
                <li>
                  <button onClick={() => scrollTo('hero')} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer" type="button">
                    Início
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollTo('beneficios')} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer" type="button">
                    Serviços
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollTo('depoimentos')} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer" type="button">
                    Depoimentos
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollTo('contato')} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer" type="button">
                    Contato
                  </button>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-heading font-semibold text-sm text-foreground-950 uppercase tracking-wider mb-4">
                {t('footer.services')}
              </h4>
              <ul className="space-y-2.5">
                <li>
                  <span className="text-sm text-foreground-600">Transporte de Cargas</span>
                </li>
                <li>
                  <span className="text-sm text-foreground-600">Carros Dedicados</span>
                </li>
                <li>
                  <span className="text-sm text-foreground-600">Redespacho</span>
                </li>
                <li>
                  <span className="text-sm text-foreground-600">Rastreamento Online</span>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-heading font-semibold text-sm text-foreground-950 uppercase tracking-wider mb-4">
                {t('footer.contact')}
              </h4>
              <ul className="space-y-3">
                <li>
                  <a href="tel:+552737236099" className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors" rel="nofollow">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-phone-line text-primary-500" />
                    </div>
                    (27) 3723-6099
                  </a>
                </li>
                <li>
                  <a href="https://wa.me/552737236099" target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-2 text-sm text-foreground-600 hover:text-accent-600 transition-colors">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-whatsapp-line text-accent-500" />
                    </div>
                    WhatsApp
                  </a>
                </li>
                <li>
                  <a href="mailto:speed@speedcourier.com.br" className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors" rel="nofollow">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line text-primary-500" />
                    </div>
                    speed@speedcourier.com.br
                  </a>
                </li>
                <li>
                  <span className="flex items-start gap-2 text-sm text-foreground-600">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                      <i className="ri-map-pin-line text-primary-500" />
                    </div>
                    Rua São Pedro, Mario Giurizatto, Colatina-ES
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-background-300/60">
        <div className="w-full px-4 md:px-6 lg:px-10 py-5">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-foreground-500 text-center sm:text-left">
              {t('footer.copyright').replace('{year}', String(year))}
            </p>
            <p className="text-xs text-foreground-500">
              {t('footer.legal')}
            </p>
            <a
              href="https://www.nxhost.com.br"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-xs text-foreground-500 hover:text-primary-600 transition-colors text-center sm:text-right"
            >
              Criação de sites e hospedagem: <span className="font-semibold text-foreground-700">www.nxhost.com.br</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}